import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Table, Button, Modal, Form, Input, InputNumber, Spin, message, Space } from "antd";
import { CopyOutlined, CheckOutlined, LoadingOutlined, EditOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import axios, { getCurrentEnv } from "../../axiosConfig";

const LEADERBOARD_NAME = "lions_roar_lb";

export default function VendorList() {
  const [allData, setAllData] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editModal, setEditModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editForm] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const vendorIdMap = useRef({});
  const [vendorCouponsMap, setVendorCouponsMap] = useState(null);
  const [vendorDatesMap, setVendorDatesMap] = useState({});
  const [copyingRow, setCopyingRow] = useState(null);
  const [copiedRow, setCopiedRow] = useState(null);

  function fetchData() {
    setLoading(true);
    axios
      .get(`/dashboard/limit/leaderboard-full-data/?l_name=${encodeURIComponent(LEADERBOARD_NAME)}`)
      .then((res) => {
        const { vendors: vendorData } = res.data;
        const rows = Object.entries(vendorData || {}).map(([name, values]) => ({
          key: name,
          vendor_name: name,
          display_name: values.display_name || "",
          city: values.city || "",
          childrean_num: values.childrean_num != null ? values.childrean_num : 0,
        }));
        rows.sort((a, b) => a.display_name.localeCompare(b.display_name));
        setAllData(rows);
        if (search.trim()) {
          const lower = search.toLowerCase();
          setData(rows.filter((r) =>
            r.vendor_name.toLowerCase().includes(lower) ||
            r.display_name.toLowerCase().includes(lower) ||
            (r.city || "").toLowerCase().includes(lower)
          ));
        } else {
          setData(rows);
        }
      })
      .catch(() => {
        message.error("Failed to load vendors");
        setAllData([]);
        setData([]);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchData();
    axios.get("/vendors/all/").then((res) => {
      const vendors = res.data.vendors || [];
      const idMap = {};
      const couponsMap = {};
      const datesMap = {};
      vendors.forEach((v) => {
        idMap[v.vendor_name] = v.id;
        couponsMap[v.vendor_name] = v.coupons_ref || 0;
        datesMap[v.vendor_name] = v.creation_date || "";
      });
      vendorIdMap.current = idMap;
      setVendorCouponsMap(couponsMap);
      setVendorDatesMap(datesMap);
    }).catch(() => {});
  }, []);

  function onSearch(value) {
    setSearch(value);
    if (!value.trim()) {
      setData(allData);
      return;
    }
    const lower = value.toLowerCase();
    setData(
      allData.filter(
        (r) =>
          r.vendor_name.toLowerCase().includes(lower) ||
          r.display_name.toLowerCase().includes(lower) ||
          (r.city || "").toLowerCase().includes(lower)
      )
    );
  }

  function openEdit(record) {
    setEditRecord(record);
    editForm.setFieldsValue({
      display_name: record.display_name,
      city: record.city,
      childrean_num: record.childrean_num,
    });
    setEditModal(true);
  }

  function submitEdit() {
    editForm.validateFields().then((values) => {
      setSaving(true);
      axios
        .post("/dashboard/limit/leaderboard-data-update/", {
          l_name: LEADERBOARD_NAME,
          vendor_name: editRecord.vendor_name,
          vendor_data: {
            display_name: values.display_name || "",
            city: values.city || "",
            childrean_num: values.childrean_num != null ? values.childrean_num : 0,
          },
        })
        .then(() => {
          message.success("Updated successfully");
          setEditModal(false);
          fetchData();
        })
        .catch((err) => {
          message.error(err.response?.data?.error || "Failed to update");
        })
        .finally(() => setSaving(false));
    });
  }

  async function copyLink(vendorName) {
    const vendorSysId = vendorIdMap.current[vendorName];
    if (!vendorSysId) {
      message.error("Vendor system ID not found");
      return;
    }
    setCopyingRow(vendorName);
    try {
      const res = await axios.get(`/coupons/vendorid/?vendor_id=${vendorSysId}`);
      const coupons = res.data || [];
      if (coupons.length === 0) {
        message.warning("No coupons found for this vendor");
        setCopyingRow(null);
        return;
      }
      const env = getCurrentEnv();
      const base = env === "dev"
        ? "https://dev-dashboard.nick.academy"
        : "https://dashboard.nick.academy";
      const url = `${base}/vendors?coupon_id=${coupons[0].coupon_id}`;
      await navigator.clipboard.writeText(url);
      setCopyingRow(null);
      setCopiedRow(vendorName);
      message.success("Link copied to clipboard");
      setTimeout(() => setCopiedRow(null), 2000);
    } catch {
      message.error("Failed to fetch coupon");
      setCopyingRow(null);
    }
  }

  const columns = [
    {
      title: "Display Name",
      dataIndex: "display_name",
      key: "display_name",
      sorter: (a, b) => a.display_name.localeCompare(b.display_name),
    },
    {
      title: "Vendor Name",
      dataIndex: "vendor_name",
      key: "vendor_name",
      sorter: (a, b) => a.vendor_name.localeCompare(b.vendor_name),
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      sorter: (a, b) => (a.city || "").localeCompare(b.city || ""),
    },
    {
      title: "Created",
      key: "creation_date",
      render: (_, record) => {
        const d = vendorDatesMap[record.vendor_name];
        if (!d) return "—";
        return new Date(d).toLocaleString();
      },
      sorter: (a, b) => {
        const da = vendorDatesMap[a.vendor_name] || "";
        const db = vendorDatesMap[b.vendor_name] || "";
        return da.localeCompare(db);
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title={copiedRow === record.vendor_name ? "Copied!" : "Copy Link"}>
            <Button
              size="small"
              icon={
                copyingRow === record.vendor_name ? <LoadingOutlined /> :
                copiedRow === record.vendor_name ? <CheckOutlined /> :
                <CopyOutlined />
              }
              onClick={() => copyLink(record.vendor_name)}
              disabled={copyingRow === record.vendor_name}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: "95%", maxWidth: 800, margin: "0 auto", padding: "24px 0" }}>
      <div style={{ marginBottom: 16 }}>
        <Link to="/create-vendor">+ Create Vendor</Link>
      </div>
      <h2 style={{ textAlign: "center", marginBottom: 24 }}>
        Vendors — {LEADERBOARD_NAME}
      </h2>

      <Input
        placeholder="Search by vendor name, display name, or city"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        allowClear
        style={{ marginBottom: 16 }}
      />

      {loading ? (
        <div style={{ textAlign: "center", marginTop: 60 }}>
          <Spin size="large" />
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <style>{`.vendor-row-error { background: #fff1f0; }
.vendor-row-error:hover > td { background: #ffccc7 !important; }`}</style>
          <Table
            dataSource={data}
            columns={columns}
            pagination={false}
            size="middle"
            rowClassName={(record) =>
              vendorCouponsMap && (
                vendorCouponsMap[record.vendor_name] === 0 ||
                !(record.vendor_name in vendorCouponsMap)
              ) ? "vendor-row-error" : ""
            }
            locale={{ emptyText: "No vendors found" }}
          />
        </div>
      )}

      <Modal
        visible={editModal}
        title={`Edit — ${editRecord?.vendor_name || ""}`}
        onCancel={() => setEditModal(false)}
        onOk={submitEdit}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item label="Vendor Name">
            <Input value={editRecord?.vendor_name || ""} disabled />
          </Form.Item>
          <Form.Item
            name="display_name"
            label="Display Name"
            rules={[{ required: true, message: "Display name is required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="city" label="City">
            <Input />
          </Form.Item>
          <Form.Item name="childrean_num" label="Children Count">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
